import { useAuth0 } from "@auth0/auth0-react"
import { mblsconnectorApi } from "src/features/mblsConnector/mblsConenctorApi"
import { meApi } from "src/features/me/meApi"
import { partnersApi } from "src/features/partners/partnersApi"
import { reportApi } from "src/features/report/reportApi"
import { thingsApi } from "src/features/things/thingsApi"
import { thingsSaApi } from "src/features/things/thingsSaApi"
import { useAppAuth } from "../auth/AuthContext"
import { useAuthStrategy } from "../auth/AuthStrategyContext"

/**
 * Unified auth status hook that provides consistent auth state and user data across all auth strategies
 * @returns Auth status with uniform interface regardless of auth provider
 */
export const useAuthStatus = () => {
  const strategy = useAuthStrategy()
  const appAuth = useAppAuth()

  // Get auth state based on strategy
  // NOTE: Hooks are called inside switch branches. This is safe because strategy
  // is determined once at app load and never changes, so the same branch always
  // executes and hooks are called in the same order every render.
  let isSignedIn = false
  let isLoaded = false
  let user: any = null
  let signOutFn: () => Promise<void> = async () => {}

  switch (strategy) {
    case "auth0": {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const auth0 = useAuth0()
      isSignedIn = auth0.isAuthenticated
      isLoaded = !auth0.isLoading
      user = auth0.user
      signOutFn = async () => {
        auth0.logout()
      }
      break
    }
    case "parent": {
      // Parent auth assumes user is always authenticated
      isSignedIn = true
      isLoaded = true
      user = null // No user data available for parent auth
      signOutFn = async () => {
        console.log(
          "Sign out called for parent auth - implement parent-specific logic",
        )
      }
      break
    }
  }

  // API instances (now strategy-aware through baseQuery)
  const apis = {
    thingsApi,
    reportApi,
    thingsSaApi,
    meApi,
    partnersApi,
    mblsconnectorApi,
  }

  return {
    // Auth provider specific state
    isSignedIn,
    isLoaded,
    user,
    // Auth provider specific user data accessors
    email: user?.email || user?.primaryEmailAddress?.emailAddress,
    imageUrl: user?.imageUrl || user?.picture,
    firstName: user?.firstName || user?.given_name,
    lastName: user?.lastName || user?.family_name,
    fullName:
      user?.fullName ||
      `${user?.firstName || user?.given_name || ""} ${user?.lastName || user?.family_name || ""}`.trim(),
    // Auth provider specific actions
    signOut: signOutFn,
    // Unified app auth state (from AuthContext)
    isAuthenticated: appAuth.isAuthenticated,
    isProcessing: appAuth.isProcessing,
    isProcessed: appAuth.isProcessed,
    isReady: appAuth.isReady,
    // Combined ready state (auth provider loaded AND app auth ready)
    isFullyReady: isLoaded && appAuth.isReady,
    // API instances for the current strategy
    apis,
  }
}
